"use client"

import { FormEvent, useEffect, useRef, useState } from "react"
import { useParams, useRouter, useSearchParams } from "next/navigation"
import { MagnifyingGlass } from "@medusajs/icons"

type ProductSearchProps = {
  /** Visual density / placement */
  variant?: "nav" | "hero" | "overlay"
  id?: string
  className?: string
  autoFocus?: boolean
  onEscape?: () => void
}

const ProductSearch = ({
  variant = "hero",
  id = "product-search",
  className = "",
  autoFocus = false,
  onEscape,
}: ProductSearchProps) => {
  const router = useRouter()
  const { countryCode } = useParams()
  const searchParams = useSearchParams()
  const urlQuery = searchParams.get("q") || ""
  const [draftQuery, setDraftQuery] = useState<string | null>(null)
  const [syncedUrlQuery, setSyncedUrlQuery] = useState(urlQuery)

  if (urlQuery !== syncedUrlQuery) {
    setSyncedUrlQuery(urlQuery)
    setDraftQuery(null)
  }

  const query = draftQuery ?? urlQuery
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus()
    }
  }, [autoFocus])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = query.trim()
    const base = `/${countryCode}/store`

    if (!trimmed) {
      router.push(base)
      return
    }

    router.push(`${base}?q=${encodeURIComponent(trimmed)}`)
  }

  const isNav = variant === "nav"
  const isOverlay = variant === "overlay"

  if (isOverlay) {
    return (
      <form
        onSubmit={handleSubmit}
        role="search"
        className={["flex w-full min-w-0 items-center gap-3", className].join(
          " "
        )}
      >
        <MagnifyingGlass className="shrink-0 text-vx-muted" />
        <label className="sr-only" htmlFor={id}>
          ค้นหาสินค้า
        </label>
        <input
          ref={inputRef}
          id={id}
          type="search"
          name="q"
          value={query}
          onChange={(event) => setDraftQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              onEscape?.()
            }
          }}
          placeholder="Search"
          autoComplete="off"
          className="min-w-0 flex-1 border-0 border-b border-vx-line bg-transparent py-2 text-[15px] text-vx-ink outline-none placeholder:text-vx-muted focus:border-vx-accent"
          data-testid={`${id}-input`}
        />
        <button
          type="submit"
          className="sr-only"
          data-testid={`${id}-submit`}
        >
          ค้นหา
        </button>
      </form>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      className={[
        "flex w-full items-stretch overflow-hidden rounded-full border border-vx-line bg-white shadow-[0_1px_2px_rgba(0,0,0,0.08)]",
        isNav ? "h-10" : "h-14 small:h-16",
        className,
      ].join(" ")}
    >
      <label className="sr-only" htmlFor={id}>
        ค้นหาสินค้า
      </label>
      <input
        ref={inputRef}
        id={id}
        type="search"
        name="q"
        value={query}
        onChange={(event) => setDraftQuery(event.target.value)}
        placeholder="ค้นหา SKU, รุ่น, หรือสเปก"
        autoComplete="off"
        className={[
          "min-w-0 flex-1 border-0 bg-transparent text-slate-900 outline-none placeholder:text-slate-500",
          isNav
            ? "px-4 text-[13px]"
            : "px-5 text-base small:px-6 small:text-[17px]",
        ].join(" ")}
        data-testid={`${id}-input`}
      />
      <button
        type="submit"
        className={[
          "shrink-0 bg-vx-accent font-mono font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#2563eb]",
          isNav
            ? "px-5 text-[11px]"
            : "px-7 text-[12px] small:px-10 small:text-[13px]",
        ].join(" ")}
        data-testid={`${id}-submit`}
      >
        ค้นหา
      </button>
    </form>
  )
}

export default ProductSearch
